/**
 * Tabs Component
 * Tabbed interface components based on Radix UI Tabs primitive.
 * @packageDocumentation
 */

import * as TabsPrimitive from '@radix-ui/react-tabs';
import { forwardRef, type ElementRef, type ComponentPropsWithoutRef } from 'react';

import { cn } from '@/lib/utils';

/** Tabs root component. */
const Tabs = TabsPrimitive.Root;

/** Tab list variant type. */
type TabsListVariant = 'default' | 'underline';

/** Tabs list container component props. */
interface TabsListProps extends ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  variant?: TabsListVariant;
}

/** Tabs list container component. */
const TabsList = forwardRef<ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        variant === 'default' &&
          'inline-flex h-9 items-center justify-center gap-1 rounded-lg bg-muted p-1 text-muted-foreground',
        variant === 'underline' && 'flex gap-8 border-b border-border',
        className,
      )}
      {...props}
    />
  ),
);
TabsList.displayName = TabsPrimitive.List.displayName;

/** Tab trigger variant type. */
type TabsTriggerVariant = 'default' | 'underline';

/** Tabs trigger props. */
interface TabsTriggerProps extends ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  count?: number;
  variant?: TabsTriggerVariant;
}

/** Tabs trigger button component with optional count badge. */
const TabsTrigger = forwardRef<ElementRef<typeof TabsPrimitive.Trigger>, TabsTriggerProps>(
  ({ count, children, className, variant = 'default', ...props }, ref) => (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        variant === 'default' &&
          'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow',
        variant === 'underline' &&
          'relative pb-3 text-sm font-medium text-muted-foreground transition-colors duration-150 hover:text-foreground focus-visible:outline-none data-[state=active]:text-primary after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:scale-x-0 after:bg-primary after:transition-transform data-[state=active]:after:scale-x-100',
        className,
      )}
      {...props}
    >
      {children}
      {count !== undefined && (
        <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs data-[state=active]:bg-primary/10">
          {count}
        </span>
      )}
    </TabsPrimitive.Trigger>
  ),
);
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

/** Tabs content panel component. */
const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
